import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import differenceInDays from 'date-fns/differenceInDays';
import fromUnixTime from 'date-fns/fromUnixTime';
import Cookies from 'js-cookie';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import SessionStorage from 'shared/helpers/sessionStorage';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  const isProduction =
    import.meta.env.NODE_ENV === 'production' ||
    window.location.hostname.endsWith('ruut.chat');
  const cookieOptions = {
    expires: differenceInDays(expiryDate, new Date()),
    path: '/',
  };
  if (isProduction) {
    cookieOptions.domain = '.ruut.chat';
  }
  const authData = JSON.stringify(response.headers);
  // Clear any existing cookies to ensure no mismatched ones
  // Cookies.remove('cw_d_session_info');
  // Cookies.remove(
  //   'cw_d_session_info',
  //   isProduction ? { domain: '.ruut.chat' } : undefined
  // );
  Cookies.set('cw_d_session_info', authData, cookieOptions);
  // Also store in localStorage as fallback
  localStorage.setItem('rld_info', authData);
  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  const isProduction =
    import.meta.env.NODE_ENV === 'production' ||
    window.location.hostname.endsWith('ruut.chat');
  const removeOptions = isProduction
    ? { domain: '.ruut.chat', path: '/' }
    : { path: '/' };
  Cookies.remove('cw_d_session_info', removeOptions);
  Cookies.remove('cw_d_session_info', { path: '/' }); // Also remove without domain in case of mismatch
  Cookies.remove('auth_data', { path: '/' });
  Cookies.remove('user', { path: '/' });
  // Clear localStorage fallback
  localStorage.removeItem('rld_info');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = () => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
